import React from 'react';
import {BrowserRouter} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {defaultUserContext, setUserInstance, UserContext} from '@src/contexts/UserContext';
import LoadingLayout from '@src/components/_layouts/Loading/LoadingLayout';
import {AppProps, AppState} from '@src/@types/components/App';
import Routes from '@src/containers/Routes/Routes';
import RenderIf from '@src/components/RenderIf/RenderIf';
import Translate from '@src/components/Translate/Translate';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';
import PopupContainer from '@src/components/Popup/PopupContainer';
import Spinner from '@src/components/Spinner/Spinner';
import {LocaleContext} from '@src/contexts/LocaleContext';
import {IUserContext} from '@src/@interfaces/contexts/IUserContext';
import queryClient from '@src/react-query-client';
import SyntheticInteractiveElementsKeyboardEventsProvider
    from '@src/components/SyntheticInteractiveElementsKeyboardEventsProvider/SyntheticInteractiveElementsKeyboardEventsProvider';
import app from '@src/helpers/app';
import detectOSName, {OS} from '@src/helpers/detectOSName';
import AsyncFileUploader from '@src/components/AsyncFileUploader/AsyncFileUploader';
import {isMobileViewport} from '@src/helpers/isMobileViewport';
import {debounce} from '@src/helpers/debounce-throttle';
import LazyChatProvider from '@views/chat/LazyChatProvider';

export default class App extends React.Component<AppProps, AppState> {
    MOBILE_VIEWPORT_CLASS_NAME = 'mobile-viewport';
    DESKTOP_VIEWPORT_CLASS_NAME = 'desktop-viewport';

    state: AppState = {
        fatalError: undefined
    };

    protected userContextValue: IUserContext = defaultUserContext;

    constructor(props: AppProps) {
        super(props);

        this.state.fatalError = props.fatalError;
        this.refreshUserContextValue();

        this.onResizeListener = debounce(this.onResizeListener.bind(this), 100);
    }

    static getDerivedStateFromError(error: Error) {
        return {fatalError: error};
    }

    componentDidMount() {
        const {booting, booted} = this.props;
        if (!booted && !booting) {
            this.props.appBootstrap();
        }

        this.refreshUserContextValue();
        this.updateBodyAdditionalClasses();

        window?.addEventListener('resize', this.onResizeListener);
    }

    componentDidUpdate() {
        const {booted, loading, loaded} = this.props;
        if (booted && !loaded && !loading) {
            this.props.appLoadRequires();
        }

        this.updateBodyAdditionalClasses();
    }

    componentWillUnmount() {
        this.clearBodyAdditionalClasses();

        window?.removeEventListener('resize', this.onResizeListener);
    }

    render() {
        this.refreshUserContextValue();

        const errorRepresentation = this.renderFatalError();

        const {booted, loaded, user} = this.props;
        if (!booted || !loaded) {
            return errorRepresentation ?? <LoadingLayout>
                <RenderIf
                        expression={booted}
                        render={() => this.renderLoader()}
                        renderElse={() => this.renderLoader('App.booting', 'Booting...')}
                />
            </LoadingLayout>;
        }

        const openedPopups = [
            errorRepresentation ? 'fatal-error' : '',
            !errorRepresentation && user.accessToken && !user.model ? 'loader' : '',
        ].filter(item => item.length);

        return <UserContext.Provider value={this.userContextValue}>
            <LocaleContext.Provider value={{locale: this.props.locale, fallbackLocale: 'en'}}>
                <QueryClientProvider client={queryClient}>
                    <AsyncFileUploader>
                        <SyntheticInteractiveElementsKeyboardEventsProvider>
                            <PopupContainer popups={{
                                'fatal-error': () => errorRepresentation,
                                'loader': this.renderLoader,
                            }} openedPopups={openedPopups}>
                                <BrowserRouter basename="/">
                                    <LazyChatProvider>
                                        <Routes/>
                                    </LazyChatProvider>
                                </BrowserRouter>
                            </PopupContainer>

                            <ReactQueryDevtools position="top-left" initialIsOpen={false} closeButtonProps={{
                                style: {left: 'auto', right: '0px'}
                            }}/>
                        </SyntheticInteractiveElementsKeyboardEventsProvider>
                    </AsyncFileUploader>
                </QueryClientProvider>
            </LocaleContext.Provider>
        </UserContext.Provider>;
    }

    private refreshUserContextValue() {
        const oldValues = {
            accessToken: this.userContextValue.accessToken,
            model: this.userContextValue.model,
            preferences: this.userContextValue.preferences,
        };

        this.userContextValue.accessToken = this.props.user.accessToken;
        this.userContextValue.model = this.props.user.model;
        // this.userContextValue.preferences = this.props.user.preferences;
        setUserInstance(app('auth').getUser());

        if (
                oldValues.accessToken !== this.userContextValue.accessToken ||
                Boolean(oldValues.model) !== Boolean(this.userContextValue.model) ||
                oldValues.preferences !== this.userContextValue.preferences
        ) {
            this.userContextValue = {...this.userContextValue};
        }
    }

    protected renderFatalError() {
        const error = this.props.fatalError ?? this.state.fatalError;

        return error ? <ErrorRepresentation error={error}/> : null;
    }

    protected renderLoader(message = 'App.loading', defaultValue = 'Loading...') {
        return <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center">
                <Spinner type="chase"/>
                <Translate
                        message={message}
                        defaultMessage={defaultValue}
                        tag="span"
                        className="loader-on-overlay__loader ms-3"
                />
            </div>
        </div>;
    }

    protected onResizeListener() {
        this.updateBodyAdditionalClasses();
    }

    private getBodyAdditionalClassesToRemove() {
        return [
            ...Object.values(OS).map(c => c + '-os'),
            this.MOBILE_VIEWPORT_CLASS_NAME,
            this.DESKTOP_VIEWPORT_CLASS_NAME,
        ];
    }

    private clearBodyAdditionalClasses(): void {
        document?.body.classList?.remove(
                ...this.getBodyAdditionalClassesToRemove(),
        );
    }

    private updateBodyAdditionalClasses(): void {
        const currentValues = [this.getOSName(), this.getViewportName()];
        const classesToRemove = this.getBodyAdditionalClassesToRemove()
                .filter(className => !currentValues.includes(className));

        document?.body.classList?.remove(...classesToRemove);
        document?.body.classList?.add(...currentValues);
    }

    private getOSName(): string {
        return String(detectOSName() ?? 'unknown').toLowerCase() + '-os';
    }

    private getViewportName(): string {
        return isMobileViewport() ?
                this.MOBILE_VIEWPORT_CLASS_NAME :
                this.DESKTOP_VIEWPORT_CLASS_NAME;
    }
}
